import {
	type ApplicationLicenseStates,
	LICENSE_STATE_ACTIVE,
} from '@jira/platform__license-state/src/license-state.tsx';
import { createResourceError } from '@jira/router-resources__utils/src/createResourceError.tsx';
import { setMark } from '@atlassian/jira-common-performance/src/marks.tsx';
// eslint-disable-next-line jira/import-whitelist
import {
	prefetchApiReadyMark,
	prefetchApiStartMark,
} from '@atlassian/jira-providers-spa-apdex-analytics/src/marks.tsx';
import type { ResourceStoreContext, RouterDataContext } from '@atlassian/react-resource-router';
import { performPostRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';

/**
 * Returns the X-Product header value for Rovo/Assist API requests when exactly one
 * license state is ACTIVE. Used for cost attribution. If zero or more than one
 * license is active, returns 'jira' (generic fallback).
 */
export const getXProductFromLicenseStates = (
	licenseStates: ApplicationLicenseStates | undefined,
): string => {
	const licenseStateKeys: (keyof ApplicationLicenseStates)[] = [
		'core',
		'software',
		'serviceDesk',
		'customerService',
		'productDiscovery',
	];

	const licenseKeyToXProduct = {
		core: 'jira-software',
		software: 'jira-software',
		serviceDesk: 'jsm',
		customerService: 'csm',
		productDiscovery: 'jpd',
	};

	if (!licenseStates) {
		return 'jira';
	}

	const activeKeys = licenseStateKeys.filter((key) => licenseStates[key] === LICENSE_STATE_ACTIVE);

	if (activeKeys.length !== 1) {
		return 'jira';
	}

	return licenseKeyToXProduct[activeKeys[0]] ?? 'jira';
};

export const fetchWorkflowBuilderProvisioning = async (
	{ match: _match }: RouterDataContext,
	{ tenantContext }: ResourceStoreContext,
): Promise<boolean> => {
	const { isAnonymous, cloudId, licenseStates } = tenantContext;

	if (isAnonymous) {
		const message = "Workflows can't be edited in anonymous mode.";
		throw new Error(message);
	}

	try {
		setMark(prefetchApiStartMark);
		await performPostRequest(
			'/gateway/api/assist/v1/agent/provisioning/ootb/jira_workflow_builder_agent',
			{
				headers: {
					'X-Product': getXProductFromLicenseStates(licenseStates),
					'X-Experience-Id': 'ai-mate',
					'Atl-Cloudid': cloudId,
				},
			},
		);
		setMark(prefetchApiReadyMark);

		// If the request is successful, we return true as this means that the workflow builder was already provisioned or has just succesfully been provisioned
		return true;

		// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		throw createResourceError(
			{
				component: 'spa.view.routes.admin-pages.workflows.workflow-builder-provisioning.getData',
				message: 'An error was thrown while prefetching workflow builder provisioning',
				name: 'ResourceError',
			},
			error,
		);
	}
};
